/* eslint-disable no-lonely-if */

// Third party
import React from 'react';
import classNames from 'classnames';

// Components
import { SelectInput, SelectOption } from '@/views/components/inputs';
import * as ProposalNS from '@/modules/proposal/Proposal';

// Utils
import { formatLocalizedString, FormattedMessage, formatMessage, LOCALE } from '@/utils/i18n';
import { filterItemsByItemSelectionFilters, updateGICItemNames, itemSelectorFiltersContainsGIC } from '@/modules/proposal/ProposalHelpers';
import ComparisonItem from '@/types/comparison-item';
import LocalizedString from '@/types/localized-string';
import { ComparisonItemCategory } from '@/redux/modules/types/comparison';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  className?: string;
  comparisonItems: ComparisonItem[];
  selectedProposalItems: ComparisonItem[];
  onSelection: (items: ComparisonItem[]) => void;
  numberOfControls: number;
  itemSelectors?: ProposalNS.ProposalItemSelectionOption[];
  singleItemShift?: boolean;
  allowedItemTypes: ProposalNS.ProposalAllowedItemType[];
  showTitle?: boolean;
};

class ProposalItemSelector extends React.PureComponent<Props> {
  private detectCategoryFromLabel = (selector?: ProposalNS.ProposalItemSelectionOption): ComparisonItemCategory | undefined => {
    if (!selector || !selector.label) {
      return undefined;
    }

    const label = selector.label[LOCALE.EN_CA] ?? selector.label[LOCALE.EN_US] ?? '';
    const labelLower = label.toLowerCase();

    if (labelLower.includes(ComparisonItemCategory.CURRENT)) {
      return ComparisonItemCategory.CURRENT;
    }

    if (labelLower.includes(ComparisonItemCategory.RECOMMENDED)) {
      return ComparisonItemCategory.RECOMMENDED;
    }

    return undefined;
  };

  private isCategoryCurrent = (category: ComparisonItemCategory | undefined): boolean => category === ComparisonItemCategory.CURRENT;

  private isCategoryRecommended = (category: ComparisonItemCategory | undefined): boolean => category === ComparisonItemCategory.RECOMMENDED;

  private hasItemWithCategory = (): boolean =>
    this.props.comparisonItems.some(
      ({ category }) => this.isCategoryCurrent(category as ComparisonItemCategory) || this.isCategoryRecommended(category as ComparisonItemCategory),
    );

  private findItemByCategory = (category: ComparisonItemCategory): ComparisonItem | undefined =>
    this.props.comparisonItems.find(item => item.category && item.category === category);

  getValidComparisonItems = (selector?: ProposalNS.ProposalItemSelectionOption): ComparisonItem[] => {
    const { comparisonItems, allowedItemTypes } = this.props;

    let filteredItems = comparisonItems.filter(item => allowedItemTypes.indexOf(item.type) !== -1);

    if (selector && selector.filters !== undefined) {
      filteredItems = filterItemsByItemSelectionFilters(filteredItems, selector.filters);
    }

    const detectedCategory = this.detectCategoryFromLabel(selector);
    if (this.isCategoryCurrent(detectedCategory)) {
      filteredItems = filteredItems.filter(item => !this.isCategoryRecommended(item.category as ComparisonItemCategory));
    } else if (this.isCategoryRecommended(detectedCategory)) {
      filteredItems = filteredItems.filter(item => !this.isCategoryCurrent(item.category as ComparisonItemCategory));
    }

    return filteredItems;
  };

  getOptions = (items: ComparisonItem[], index: number, filters?: ProposalNS.ProposalItemSelectionFilter[]) => {
    const { singleItemShift } = this.props;

    const out =
      singleItemShift && index === 0
        ? [
            <SelectOption key={-1} value={null}>
              {formatMessage('Common.None')}
            </SelectOption>,
          ]
        : [];

    let itemsToDisplay = items;

    if (filters !== undefined && itemSelectorFiltersContainsGIC(filters)) {
      itemsToDisplay = updateGICItemNames(items);
    }

    itemsToDisplay.forEach(selectionOption => {
      out.push(
        <SelectOption key={selectionOption._id} value={selectionOption._id}>
          <span>{formatLocalizedString(selectionOption.name)}</span>
        </SelectOption>,
      );
    });

    return out;
  };

  getSelectedItemAtIndex = (index: number): string | null => {
    const { selectedProposalItems, singleItemShift, itemSelectors } = this.props;
    const localIndex: number = singleItemShift && selectedProposalItems.length === 1 ? index - 1 : index;

    if (localIndex < 0) {
      return null;
    }

    const detectedCategory = this.detectCategoryFromLabel(itemSelectors?.[index]);

    const categoryItem = this.findItemByCategory(detectedCategory!);
    if (categoryItem) {
      return categoryItem._id;
    }

    return selectedProposalItems[localIndex] !== undefined ? selectedProposalItems[localIndex]._id : null;
  };

  disableSelection = (selector?: ProposalNS.ProposalItemSelectionOption) => {
    const validItems = this.getValidComparisonItems(selector);
    const hasDisabled = this.hasItemWithCategory();
    const threshold = hasDisabled ? 0 : 1;
    if (validItems.length === threshold) {
      return true;
    }

    // If a category is detected from the label, disable selection if there is a selection via item category
    const detectedCategory = this.detectCategoryFromLabel(selector);
    if (this.findItemByCategory(detectedCategory as ComparisonItemCategory)) {
      return true;
    }

    return false;
  };

  onSelection = (index: number) => (newID: string | null) => {
    const { selectedProposalItems, comparisonItems, itemSelectors } = this.props;

    const newChoice: ComparisonItem | undefined = comparisonItems.find(item => item._id === newID);

    const selector = itemSelectors?.[index];
    if (this.disableSelection(selector)) {
      return;
    }

    if (newChoice === undefined) {
      this.props.onSelection([selectedProposalItems[1]]);
    } else {
      const currentSelectionCount: number = selectedProposalItems.length;
      // const otherIndex: number = (index + 1) % 2;

      if (index === 0) {
        if (currentSelectionCount === 1) {
          // we are replacing 'none' so we move the selectedProposalItem in position 0 to 1
          if (newChoice._id === selectedProposalItems[0]._id) {
            const availableItems = this.getValidComparisonItems().filter(item => item._id !== selectedProposalItems[0]._id);
            this.props.onSelection([selectedProposalItems[0], availableItems[0]]);
          } else {
            this.props.onSelection([newChoice, selectedProposalItems[0]]);
          }
        } else {
          // we are replacing the selectedProposalItem in position 0
          if (newChoice._id === selectedProposalItems[1]._id) {
            // same as item in 1, flip them
            this.props.onSelection([selectedProposalItems[1], selectedProposalItems[0]]);
          } else {
            this.props.onSelection([newChoice, selectedProposalItems[1]]);
          }
        }
      } else {
        if (currentSelectionCount === 1) {
          // first item is 'none' and we change the selectedProposalItem in position 0
          this.props.onSelection([newChoice]);
        } else {
          // we are replacing the selectedProposalItem in position 1
          if (newChoice._id === selectedProposalItems[0]._id) {
            // same as item in 0, flip them
            this.props.onSelection([selectedProposalItems[1], selectedProposalItems[0]]);
          } else {
            this.props.onSelection([selectedProposalItems[0], newChoice]);
          }
        }
      }
    }
  };

  render() {
    const { numberOfControls, itemSelectors, className, showTitle = true } = this.props;

    return (
      <div className={classNames('proposal-item-selector', className)}>
        {showTitle && (
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerXs">
            <FormattedMessage id="Proposal.Tabs.ItemSelector.Title" />
          </Typography>
        )}

        {Array.from(Array(numberOfControls).keys()).map((count, index) => {
          const selector = itemSelectors?.[index];
          if (!selector) {
            return null;
          }

          const labelStr: LocalizedString = selector.label;
          const label: string | undefined = labelStr ? formatLocalizedString(labelStr) : undefined;
          const validItems = this.getValidComparisonItems(selector);

          return (
            <div key={index} className="item-option" data-private data-dd-privacy="mask">
              {label && <div className="item-title">{label}</div>}

              <SelectInput
                placeholder="---"
                disabled={this.disableSelection(selector)}
                onValueChange={this.onSelection(count)}
                key={count}
                selected={this.getSelectedItemAtIndex(count)}
                compact
              >
                {this.getOptions(validItems, count, selector.filters)}
              </SelectInput>
            </div>
          );
        })}
      </div>
    );
  }
}

export default ProposalItemSelector;
